import { IFilterService } from "angular";
import { IGridColumnDefinition, IGridSortingDefinition, getPersistedScreenSettings, setPersistedScreenSettings } from "@gtmhub/shared/components/grid-filtering-bar";
import { AssigneeSelectorType } from "@webapp/assignees/models/assignee.models";
import { ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { ISessionsIdMap, Session } from "@webapp/sessions/models/sessions.model";
import { ITeam } from "../teams";
import { IdMap } from "../util";
import { ContributorTypeEnum, GoalChildType, IAllGoal, IGoal, IGoalChildReorderPayload, IOkrGridColumn, IPolicy, ISessionNode, OkrGridContributor } from "./models";

export function constructSessionsTree(currentSessionId: string, sessionIdMap: ISessionsIdMap): ISessionNode {
  if (!currentSessionId || !sessionIdMap) {
    return null;
  }

  const currentSession = sessionIdMap[currentSessionId];
  if (!currentSession) {
    throw new Error(`Cannot find current session ${currentSessionId} in the map`);
  }

  const getParent = (session: Session): Session => (session.parentId ? sessionIdMap[session.parentId] : null);

  // Construct a map for session IDs to their node in the tree
  const nodes: { [sessionId: string]: ISessionNode } = {};
  const sessions = Object.values(sessionIdMap);

  for (const session of sessions) {
    if (!nodes[session.id]) {
      nodes[session.id] = {
        session,
        children: [],
      };
    }

    const sessionNode: ISessionNode = nodes[session.id];

    const parent = getParent(session);
    if (parent) {
      if (!nodes[parent.id]) {
        nodes[parent.id] = {
          session: parent,
          children: [],
        };
      }

      nodes[parent.id].children.push(sessionNode);
    }
  }

  // Find the root of the tree that contains the current session
  let parent = currentSession;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const newParent = getParent(parent);
    if (!newParent) {
      break;
    }

    parent = newParent;
  }

  return nodes[parent.id];
}

export const isFilterApplied = (...args: string[]): boolean => {
  return args.some((arg) => arg);
};

const columnsFieldLabelMap = {
  ownerIds: "Owners",
  name: "Objective name",
  krs: "Key Results",
  krsAndChildObjs: "Key Results and Child Objectives",
  attainment: "Progress",
  description: "Description",
  designScore: "OKR-design score",
};

export const defaultOkrGridColumns = (): IOkrGridColumn[] => [
  {
    field: "ownerIds",
    label: columnsFieldLabelMap.ownerIds,
    visible: true,
    isCustom: false,
  },
  {
    field: "name",
    label: columnsFieldLabelMap.name,
    visible: true,
    isCustom: false,
  },
  {
    field: "krsAndChildObjs",
    label: columnsFieldLabelMap.krsAndChildObjs,
    visible: true,
    isCustom: false,
  },
  {
    field: "attainment",
    label: columnsFieldLabelMap.attainment,
    visible: true,
    isCustom: false,
  },
  {
    field: "description",
    label: columnsFieldLabelMap.description,
    visible: false,
    isCustom: false,
  },
  {
    field: "designScore.totalPoints",
    label: columnsFieldLabelMap.designScore,
    visible: true,
    isCustom: false,
  },
];

// be careful when modifying this method and especially fixPersistedColumns part of it
// if this method does not return full set of all available columns, no matter visible or not, error may be thrown in goals grid
export const getOkrGridColumns = (screenPath: string[], customFields: ICustomField[]): IOkrGridColumn[] => {
  const fullSet = defaultOkrGridColumns();

  const persistedSharedViewOptions = getPersistedScreenSettings(screenPath, "sharedViewOptions") || {};

  const persistedColumns = persistedSharedViewOptions.okrGridColumns;

  for (let i = 0; i < customFields.length; i++) {
    fullSet.push({
      field: customFields[i].name,
      label: customFields[i].label,
      visible: true,
      isCustom: true,
    });
  }

  if (persistedColumns) {
    modifyPersistedColumnsBasedOnFeatures(persistedColumns);

    const persistedColumnsWithFixedLabels = persistedColumns.map((col) => ({ ...col, label: columnsFieldLabelMap[col.field] || col.label }));
    return fixPersistedColumns(persistedColumnsWithFixedLabels, fullSet).map((column) => setIsSortable(column, customFields));
  } else {
    for (let c = 0; c < fullSet.length; c++) {
      fullSet[c].visible = true;
    }

    persistedSharedViewOptions.okrGridColumns = fullSet;
    setPersistedScreenSettings(screenPath, "sharedViewOptions", persistedSharedViewOptions);
    return fullSet.map((column) => setIsSortable(column, customFields));
  }
};
const setIsSortable = (column: IOkrGridColumn, customFields: ICustomField[]): IOkrGridColumn => {
  const notSortableVisualizationKind = ["gtmhub.multiselect", "gtmhub.selectassignee"];
  if (column.isCustom) {
    const customField = customFields.find((field) => field.name === column.field);
    return { ...column, isSortable: !notSortableVisualizationKind.includes(customField.visualizationKind) };
  } else {
    return { ...column, isSortable: true };
  }
};
const fixPersistedColumns = (persisted: IOkrGridColumn[], fullSet: IOkrGridColumn[]): IOkrGridColumn[] => {
  const fixedColumns: IOkrGridColumn[] = [];
  const fullSetDictionary = {};
  const persistedSetDictionary = {};

  for (let c = 0; c < fullSet.length; c++) {
    fullSetDictionary[fullSet[c].field] = fullSet[c].label;
  }

  for (let c = 0; c < persisted.length; c++) {
    persistedSetDictionary[persisted[c].field] = persisted[c].label;
  }

  for (const c in persisted) {
    if (fullSetDictionary[persisted[c].field]) {
      const el: IOkrGridColumn = {
        field: persisted[c].field,
        label: persisted[c].label,
        visible: persisted[c].visible,
        isCustom: persisted[c].isCustom,
      };
      fixedColumns.push(el);
    }
  }

  for (const c in fullSet) {
    if (!persistedSetDictionary[fullSet[c].field]) {
      const el = {
        field: fullSet[c].field,
        label: fullSet[c].label,
        visible: false,
        isCustom: fullSet[c].isCustom,
      };
      fixedColumns.push(el);
    }
  }
  return fixedColumns;
};

export const convertColumnToColumnDefinition = (column: IOkrGridColumn, $filter: IFilterService): IGridColumnDefinition => ({
  fieldName: column.field,
  displayName: $filter<(column: IOkrGridColumn) => string>("localizeGridColName")(column),
  trackingName: column.label,
});

export const convertColumnToSortingDefinition = (column: IOkrGridColumn, $filter: IFilterService): IGridSortingDefinition => ({
  fieldName: convertColumnToSortingDefinitionFieldName(column),
  displayName: $filter<(column: IOkrGridColumn) => string>("localizeGridColName")(column),
  trackingName: column.label,
  isCustomField: column.isCustom,
});

const convertColumnToSortingDefinitionFieldName = (column: IOkrGridColumn): string => {
  if (column.isCustom) {
    return `customFields.${column.field}`;
  } else {
    return column.field === "ownerId" ? "assignee.name" : column.field === "ownerIds" ? "assignees.name" : column.field;
  }
};

const modifyPersistedColumnsBasedOnFeatures = (persistedColumns: IOkrGridColumn[]): void => {
  const ownerCol = persistedColumns.find((col) => col.field === "ownerId");
  if (ownerCol) {
    ownerCol.field = "ownerIds";
  }

  const okrsCol = persistedColumns.find((col) => col.field === "krs");
  if (okrsCol) {
    okrsCol.field = "krsAndChildObjs";
  }
};

export const getDefaultGoalBase = (): Partial<IGoal> => ({
  ownerIds: [],
  description: "",
  name: "",
  attainmentTypeString: "average_kr",
  dateFrom: "",
  dateTo: "",
  customFields: {},
});

export const getGoalNamePolicy = (policies: IPolicy[]): IPolicy => policies.find((policy: IPolicy) => policy.fieldName === "Name");

const getDefaultTeamAssigneeId = (params: { teams: ITeam[]; currentUserId: string }): string => {
  const teamsSortedByName = params.teams.sort((a, b) => a.name.localeCompare(b.name));
  const userIsManagerOrMemberTeams = teamsSortedByName.filter(
    (team) => team.manager === params.currentUserId || (team.members && team.members.includes(params.currentUserId))
  );

  // The goal should be assigned to a team which the current user is a part or is manager of
  if (userIsManagerOrMemberTeams.length) {
    return userIsManagerOrMemberTeams[0].id;
  }

  // If the current user is not a part of any team, the first team sorted alphabetically should be the assignee team
  if (teamsSortedByName.length) {
    return teamsSortedByName[0].id;
  }

  // Edge case - if there are no teams at all in the account, the objective is assigned to the current user even tho
  // "Who can own objective" setting is set to teams only
  return params.currentUserId;
};

export const getDefaultAssigneeId = (params: { teams: ITeam[]; currentUserId: string; assignObjectivePermissions: AssigneeSelectorType }): string => {
  return params.assignObjectivePermissions === "team" ? getDefaultTeamAssigneeId(params) : params.currentUserId;
};

export const buildParentChildrenIdMap = (goals: IAllGoal[], children: IAllGoal[] = []): IdMap<OkrGridContributor[]> => {
  const parentChildrenIdMap: IdMap<OkrGridContributor[]> = {};

  // map metrics
  for (const goal of goals) {
    parentChildrenIdMap[goal.id] = parentChildrenIdMap[goal.id] || [];
    if (goal.metrics && goal.metrics.length) {
      parentChildrenIdMap[goal.id].push(...goal.metrics.map((metric) => ({ ...metric, childType: ContributorTypeEnum.metric } as OkrGridContributor)));
    }
  }

  // map goals
  for (const child of children) {
    parentChildrenIdMap[child.parentId].push({ ...child, childType: ContributorTypeEnum.goal });
  }

  return parentChildrenIdMap;
};

type Item = { id: string; childType: GoalChildType };

export const getChildReorderPayload = (goalId: string, options: { items: Item[]; item: Item; oldIndex: number; newIndex: number }): IGoalChildReorderPayload => {
  const movedItem: IGoalChildReorderPayload = {
    parentId: goalId,
    moved: {
      id: options.item.id,
      type: options.item.childType,
    },
  };

  const firstElementIndex = 0;
  const lastElementIndex = options.items.length - 1;

  switch (options.newIndex) {
    case firstElementIndex: {
      movedItem.next = {
        id: options.items[firstElementIndex].id,
        type: options.items[firstElementIndex].childType,
      };
      break;
    }
    case lastElementIndex: {
      movedItem.previous = {
        id: options.items[lastElementIndex].id,
        type: options.items[lastElementIndex].childType,
      };
      break;
    }
    default: {
      let indexPrevious: number;
      let indexNext: number;

      if (options.oldIndex < options.newIndex) {
        indexPrevious = options.newIndex;
        indexNext = options.newIndex + 1;
      } else {
        indexPrevious = options.newIndex - 1;
        indexNext = options.newIndex;
      }

      movedItem.previous = {
        id: options.items[indexPrevious].id,
        type: options.items[indexPrevious].childType,
      };
      movedItem.next = {
        id: options.items[indexNext].id,
        type: options.items[indexNext].childType,
      };
    }
  }

  return movedItem;
};

export const goalIsNeitherObfuscatedNorLocked = (goal: IGoal): boolean => {
  return !goal.obfuscated && !goal.locked;
};

export const getParentState = (stateName: string): string => {
  if (stateName.includes(".all.")) {
    return "Grid";
  }
  if (stateName.includes("alignmentSidebar.tree")) {
    return "Alignment tree";
  }
  if (stateName.includes(".nested")) {
    return "Nested view";
  }
  if (stateName.includes(".home.") || stateName.includes(".msTeamsHome.")) {
    return "Home";
  }
  if (stateName.includes(".insightboard.")) {
    return "Insightboard";
  }
  if (stateName.includes("gtmhub.list")) {
    return "List";
  }
  if (stateName.includes("gtmhub.whiteboard")) {
    return "Whiteboard";
  }
  return "Other";
};
